import AlertMessage from '@components/alerts/AlertMessage';
import Button from '@components/button/Button';
import { ContactUs } from '@components/contactUsLink/ContactUs';
import InputWithFormItem from '@components/inputWithFormItem/InputWithFormItem';
import { CenterPanel } from '@components/layout/CenterPanel/CenterPanel';
import { SurfaceContainer } from '@components/layout/surfaceContainer/SurfaceContainer';
import { BrandLogo } from '@components/logos/brandLogo/BrandLogo';
import { Divider, Form, Space, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { MdArrowBack, MdOutlineMailOutline } from 'react-icons/md';
import { Link } from 'react-router-dom';

import { firebasePostApi } from '../../api/firebaseApi';
import { trackForgotPassword } from '../../helpers/analytics';

const { Title, Text } = Typography;

const RESEND_DELAY_SECONDS = 120;

export const ResetPassword: React.FC = () => {
  const { t } = useTranslation('common', {
    keyPrefix: 'loginAndSignup',
  });

  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const [isResendEmailAvailable, setIsResendEmailAvailable] = useState(false);
  const [remainingTimeToResendEmail, setRemainingTimeToResendEmail] =
    useState(RESEND_DELAY_SECONDS);

  useEffect(() => {
    let timer: any = null;
    if (success && !isResendEmailAvailable) {
      setRemainingTimeToResendEmail(RESEND_DELAY_SECONDS);
      setIsResendEmailAvailable(false);
      timer = setInterval(() => {
        setRemainingTimeToResendEmail((prev) => {
          if (prev <= 1) {
            clearInterval(timer!);
            setIsResendEmailAvailable(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isResendEmailAvailable, success]);

  const handleSubmit = async (userInfo: any) => {
    setLoading(true);
    setIsError(false);
    setEmail(userInfo.email);

    try {
      await firebasePostApi('/user/reset-password', { email: userInfo.email });
      setSuccess(true);

      // Track forgot password request
      trackForgotPassword(userInfo.email);
    } catch (_error) {
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = () => {
    setRemainingTimeToResendEmail(120);
    setIsResendEmailAvailable(false);
  };

  const onClickChangeEmail = () => {
    setEmail(null);
    setSuccess(false);
    setIsError(false);
    setIsResendEmailAvailable(false);
    setRemainingTimeToResendEmail(RESEND_DELAY_SECONDS);
  };

  const renderResetPasswordForm = () => (
    <>
      <Title level={2} className="!tw-m-0 tw-text-center">
        {t('resetPasswordHeader')}
      </Title>

      <Text className="tw-text-[--color-text-weaker] tw-flex tw-text-center">
        {t('resetPasswordSubHeader')}
      </Text>
      <Form
        requiredMark
        layout="vertical"
        className="tw-w-full"
        onFinish={handleSubmit}
      >
        <InputWithFormItem
          inputName="email"
          size="middle"
          placeholderText={t('emailInputPlaceholder')}
          rules={[
            {
              required: true,
              message: t('emailIsRequired'),
            },
            { type: 'email', message: t('emailIsNotValid') },
          ]}
          PrefixIcon={MdOutlineMailOutline}
        />

        <Button
          submit
          fullWidth
          type="primary"
          label={t('sendInstructions')}
          loading={loading}
        />
      </Form>
    </>
  );

  const renderResetEmailSentSuccess = () => {
    const remainingTimeText = isResendEmailAvailable
      ? ''
      : `(${remainingTimeToResendEmail})`;

    return (
      <>
        <Title level={2} className="!tw-m-0 tw-text-center">
          {t('verifyEmailHeader')}
        </Title>

        <div className="tw-text-center">
          <Trans
            components={{
              text: <Text className="tw-text-[var(--color-text-weaker)]" />,
              color: <strong style={{ color: 'var(--color-text-primary)' }} />,
            }}
          >
            {t('verifyEmailSubHeader', {
              email,
            })}
          </Trans>
        </div>

        <div className="tw-flex tw-justify-center tw-my-3">
          <Text className="tw-text-center">
            {t('havingTroubleVerifyEmail')}
          </Text>
        </div>

        <Button
          fullWidth
          size="middle"
          type="primary"
          onClick={handleResendEmail}
          disabled={!isResendEmailAvailable}
          label={`${t('sendEmailAgainCtaBtn')} ${remainingTimeText}`}
        />

        <Button
          fullWidth
          size="middle"
          type="outline"
          label={t('changeEmailBtn')}
          onClick={onClickChangeEmail}
        />
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>{t('resetPasswordHeader')}</title>
      </Helmet>
      <CenterPanel>
        <div>
          {isError && (
            <AlertMessage
              className="tw-my-2"
              showIcon
              message={t('somethingWentWrong')}
            />
          )}

          <SurfaceContainer maxWidth={530} noBorder>
            <Space orientation="vertical" size="small" className="tw-p-[16px]">
              <BrandLogo
                size={56}
                wrapperClassName="tw-flex tw-justify-center"
              />

              {success
                ? renderResetEmailSentSuccess()
                : renderResetPasswordForm()}

              <Link
                to="/login"
                className="tw-flex tw-justify-center tw-items-center tw-gap-1 tw-py-1 tw-text-[14px] tw-font-semibold !tw-text-[var(--color-secondary)] hover:tw-underline"
              >
                <MdArrowBack size={16} />
                {t('backToSignIn')}
              </Link>

              <Divider className="!tw-my-1 !tw-border-[var(--color-border-white)]" />

              <ContactUs
                wrapperClass="tw-flex tw-justify-center"
                page="reset-password"
              />
            </Space>
          </SurfaceContainer>
        </div>
      </CenterPanel>
    </>
  );
};
